import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { ArrowLeft, AlertTriangle, Bot, User as UserIcon } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface TurnDetail {
  id: string;
  questionId: string | null;
  profileKey: string | null;
  promptText: string;
  rawAnswer: string | null;
  parsedValue: unknown;
  answerQuality: string | null;
  reasoning: string | null;
  skipped: boolean;
  askedAt: string;
  answeredAt: string | null;
}

interface ConversationDetail {
  id: string;
  bankId: string;
  bankName: string;
  leadId: string | null;
  channel: string;
  contactEmail: string | null;
  contactPhone: string | null;
  contactName: string | null;
  language: string;
  profile: Record<string, unknown>;
  scoreTotal: string;
  confidence: string | null;
  status: string;
  verdict: string | null;
  overrideVerdict: string | null;
  overrideReason: string | null;
  consentAt: string | null;
  consentState: string | null;
  lastActivityAt: string;
  finalizedAt: string | null;
  abandonedReason: string | null;
  turns: TurnDetail[];
}

const VERDICT_CLASSES: Record<string, string> = {
  QUALIFIED: 'bg-green-900/40 text-green-200',
  DISQUALIFIED: 'bg-red-900/40 text-red-200',
  NEEDS_HUMAN_REVIEW: 'bg-amber-900/40 text-amber-200',
};

export function QualificationConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [conv, setConv] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<ConversationDetail>(`/qualification/conversations/${id}`)
      .then(({ data }) => {
        if (!cancelled) setConv(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, refresh]);

  if (loading || !conv) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  const effectiveVerdict = conv.overrideVerdict ?? conv.verdict;
  const profileKeys = Object.keys(conv.profile ?? {});

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <Link
        to="/qualification/conversations"
        className="inline-flex items-center gap-1 text-sm text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Back to conversations
      </Link>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              {conv.contactName ?? conv.contactEmail ?? 'Anonymous contact'}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Bank <strong>{conv.bankName}</strong> · channel{' '}
              <code className="rounded bg-muted px-1">{conv.channel}</code> ·
              language <code className="rounded bg-muted px-1">{conv.language}</code>
            </p>
            {(conv.contactEmail || conv.contactPhone) && (
              <p className="mt-1 text-sm text-muted-foreground">
                {conv.contactEmail && <span>{conv.contactEmail}</span>}
                {conv.contactEmail && conv.contactPhone && ' · '}
                {conv.contactPhone && <span>{conv.contactPhone}</span>}
              </p>
            )}
          </div>
          <div className="text-right">
            {effectiveVerdict ? (
              <span
                className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${
                  VERDICT_CLASSES[effectiveVerdict] ?? 'bg-muted'
                }`}
              >
                {effectiveVerdict}
                {conv.overrideVerdict && ' (override)'}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">No verdict yet</span>
            )}
            <p className="mt-2 text-sm">
              Score{' '}
              <strong className="tabular-nums">
                {Number(conv.scoreTotal).toFixed(3)}
              </strong>
              {conv.confidence && (
                <>
                  {' '}· conf{' '}
                  <strong className="tabular-nums">
                    {Number(conv.confidence).toFixed(2)}
                  </strong>
                </>
              )}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => setOverrideOpen(true)}
            >
              Override verdict
            </Button>
          </div>
        </div>

        {conv.consentState === 'REFUSED' && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-red-800 bg-red-950/40 p-3 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div>
              <strong>Consent refused.</strong> Sensitive questions were not
              asked. The conversation was routed to human review.
            </div>
          </div>
        )}

        {conv.abandonedReason && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-800 bg-amber-950/40 p-3 text-sm text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div>
              <strong>Abandoned</strong> ({conv.abandonedReason}).
            </div>
          </div>
        )}

        {conv.overrideReason && (
          <div className="mt-3 rounded-md border bg-muted/40 p-3 text-sm">
            <strong>Operator override reason:</strong> {conv.overrideReason}
          </div>
        )}
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Captured profile
        </h2>
        {profileKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No structured fields captured yet.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {profileKeys.map((key) => (
              <div key={key}>
                <dt className="text-xs uppercase text-muted-foreground">{key}</dt>
                <dd className="font-mono">{formatValue(conv.profile[key])}</dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Transcript ({conv.turns.length} turn{conv.turns.length === 1 ? '' : 's'})
        </h2>
        {conv.turns.length === 0 ? (
          <p className="text-sm text-muted-foreground">No turns yet.</p>
        ) : (
          <div className="space-y-4">
            {conv.turns.map((turn) => (
              <div key={turn.id} className="space-y-2">
                <div className="flex items-start gap-3">
                  <Bot className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1 rounded-md bg-muted/40 p-3 text-sm">
                    <p>{turn.promptText}</p>
                    {turn.profileKey && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        target field <code>{turn.profileKey}</code>
                      </p>
                    )}
                  </div>
                </div>
                {turn.rawAnswer != null || turn.skipped ? (
                  <div className="flex items-start gap-3">
                    <UserIcon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="flex-1 rounded-md border p-3 text-sm">
                      <p className={turn.skipped ? 'italic text-muted-foreground' : ''}>
                        {turn.skipped ? '(skipped)' : turn.rawAnswer}
                      </p>
                      {turn.reasoning && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          <strong>reasoning:</strong> {turn.reasoning}
                          {turn.answerQuality != null && (
                            <>
                              {' '}· quality{' '}
                              <span className="tabular-nums">
                                {Number(turn.answerQuality).toFixed(2)}
                              </span>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      {overrideOpen && (
        <OverrideDialog
          conversationId={conv.id}
          currentVerdict={effectiveVerdict}
          onClose={() => setOverrideOpen(false)}
          onSaved={() => {
            setOverrideOpen(false);
            setRefresh((x) => x + 1);
          }}
        />
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function OverrideDialog({
  conversationId,
  currentVerdict,
  onClose,
  onSaved,
}: {
  conversationId: string;
  currentVerdict: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [verdict, setVerdict] = useState<'QUALIFIED' | 'DISQUALIFIED' | 'NEEDS_HUMAN_REVIEW'>(
    (currentVerdict as 'QUALIFIED' | 'DISQUALIFIED' | 'NEEDS_HUMAN_REVIEW') ?? 'NEEDS_HUMAN_REVIEW',
  );
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/qualification/conversations/${conversationId}/override`, {
        verdict,
        reason: reason.trim(),
      });
      onSaved();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? 'Failed to save override.');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface-3 p-6 text-on-surface shadow-lg">
        <h2 className="mb-3 text-lg font-semibold">Override verdict</h2>
        <p className="mb-3 text-sm text-muted-foreground">
          Operator override supersedes the agent's verdict for downstream
          consumers. The override is audited.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">New verdict</label>
            <select
              value={verdict}
              onChange={(e) =>
                setVerdict(
                  e.target.value as 'QUALIFIED' | 'DISQUALIFIED' | 'NEEDS_HUMAN_REVIEW',
                )
              }
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="QUALIFIED">Qualified</option>
              <option value="DISQUALIFIED">Disqualified</option>
              <option value="NEEDS_HUMAN_REVIEW">Needs human review</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Reason</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border bg-background p-2 text-sm"
              placeholder="Why are you overriding the agent?"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save override'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
